import { useEffect, useRef, useState } from 'react';
import { MOCK, isSchemaPending } from '../lib/supabase.js';
import * as db from '../lib/data.js';
import NameTh from '../components/NameTh.jsx';
import OriginFromFile from '../components/OriginFromFile.jsx';
import { hasThai } from '../lib/naming.js';
import { hasMaterial } from '../lib/materials.js';
import DbPending from '../components/DbPending.jsx';
import { AddLineItemModal } from '../components/Modals.jsx';
import { useSigner, SignerEditor } from '../components/Signer.jsx';
import { csvToRecords } from '../lib/csv.js';
import ImportInvoiceModal from '../components/ImportInvoiceModal.jsx';
import { DOC_HEAD } from '../lib/company.js';
import { buildDossierXlsx } from '../lib/dossier-xlsx.js';
import { MaterialGapButton, useMaterialsReady } from '../components/MaterialEnqueueModal.jsx';
import PermitPanel, { PermitChips, usePermitIndex } from '../components/PermitPanel.jsx';
import { checkCard, summarizePermits, agencyCardState, permitPill } from '../lib/permit-flags.js';

// TASK 3 — Product Dossier ส่ง broker: 1 dossier = 1 consignment = 1 invoice
// A4 print-ready + completeness gate + ช่องว่างให้ broker กรอก HS

// นำเข้ารายการจากไฟล์ invoice/packing (SKU + qty) → auto-match คลัง
const INVOICE_COLMAP = {
  sku: 'sku', รหัส: 'sku', รหัสสินค้า: 'sku', 'itemcode': 'sku', code: 'sku',
  qty: 'qty', quantity: 'qty', จำนวน: 'qty', 'ปริมาณ': 'qty',
  description: 'description', desc: 'description', ชื่อ: 'description', รายละเอียด: 'description', 'ชื่อสินค้า': 'description',
  unit: 'unit', หน่วย: 'unit',
  // ราคา + ของมาเป็นกล่อง — ใส่ไว้แล้วหน้าแบรนด์เทียบกับ "ราคาที่ตกลง" ในใบสั่งซื้อให้เอง (deploy-28)
  unit_price: 'unit_price', unitprice: 'unit_price', price: 'unit_price', ราคา: 'unit_price',
  'ราคาต่อหน่วย': 'unit_price', 'ราคา/หน่วย': 'unit_price', amount: 'unit_price',
  pack_size: 'pack_size', packsize: 'pack_size', pack: 'pack_size', 'ชิ้นต่อหน่วย': 'pack_size',
  'ต่อกล่อง': 'pack_size', 'กล่องละ': 'pack_size', ctn: 'pack_size',
};
const INVOICE_TEMPLATE = 'sku,qty,unit,unit_price,pack_size,description\n' +
  'TC-095-01,10,ชิ้น,12000,1,WINGFORT OUTER TENT\n' +
  'NGN-32OZ,20,กล่อง,1800,6,Wide Mouth 32oz (1 กล่อง = 6 ขวด — ราคาเป็นต่อกล่อง)\n';

function isElectrical(li) {
  const s = `${li.description ?? ''} ${li.product_classification?.name_en ?? ''}`.toLowerCase();
  return /power|charger|batt|led|lamp|light|เตาไฟฟ้า|ชาร์จ|แบต/.test(s)
    || !!li.product_classification?.power_spec;
}

function ready(items) {
  return items.length > 0 && items.every(li => {
    const c = li.product_classification;
    // ชื่อไทยต้องเป็นภาษาไทยจริง (ศุลกากรใช้) — ช่องที่ยังเป็นชื่ออังกฤษ = ยังไม่ครบ · กติกาเดียวกับ db.cardGaps
    return c && (c.photos ?? []).length > 0 && hasThai(c.name_th) && c.what_is_it &&
      hasMaterial(c) && c.usage_desc && c.origin_country && c.brand &&
      (!isElectrical(li) || c.power_spec);
  });
}

// แผง "ยังไม่ครบ" — เดิมยัดชื่อทุกรายการเป็นย่อหน้าเดียว พอ invoice มี 114 บรรทัด
// กลายเป็นกำแพงข้อความที่อ่านไม่ออกและกดอะไรไม่ได้ (เจอจริง 25 ต.ค. 2569 ตู้ SP-261015)
// ⇒ แยกเป็น 2 กองตามสิ่งที่ต้องทำจริง แล้วให้ทางเดินต่อทุกกอง
function GapPanel({ items, missing, consignment, busy, onMakeStubs, onPullPd, onPickOrigin, onOpenProduct, msg, aiOn, onMessage, onOpenMaterials }) {
  // จำว่ากางรายการไว้ (ต่อใบ ในแท็บนี้) — งานหลักคือ กดรายการ → ไปแก้ที่หน้าสินค้า → ย้อนกลับมาทำรายการถัดไป
  //   หน้านี้เริ่มใหม่ทุกครั้งที่ย้อนกลับมา ถ้าไม่จำ ต้องกด "ดูรายการ" ใหม่ทุกรอบ (รีวิว 28 ต.ค. 69)
  const openKey = `ib_gap_open:${consignment?.id ?? ''}`;
  const [open, setOpenRaw] = useState(() => { try { return sessionStorage.getItem(openKey) === '1'; } catch { return false; } });
  const setOpen = (f) => setOpenRaw(o => {
    const v = typeof f === 'function' ? f(o) : f;
    try { sessionStorage.setItem(openKey, v ? '1' : '0'); } catch { /* โหมดส่วนตัว */ }
    return v;
  });
  const noCard = missing.filter(li => !li.product_classification);
  const partial = missing.filter(li => li.product_classification);
  const noOrigin = partial.filter(li => !li.product_classification.origin_country).length;
  const noMaterial = new Set(partial.filter(li => !String(li.product_classification.material ?? '').trim()).map(li => li.product_classification.id)).size;

  // ช่องไหนขาดกี่รายการ — บอกเป็นตัวเลข จะได้รู้ว่าควรลงแรงตรงไหนก่อน
  const FIELDS = [
    ['photos', 'รูป', c => !(c.photos ?? []).length],
    ['name_th', 'ชื่อไทย', c => !hasThai(c.name_th)],
    ['what_is_it', 'คืออะไร', c => !c.what_is_it],
    ['material', 'วัสดุ', c => !hasMaterial(c)],
    ['usage_desc', 'ใช้ทำอะไร', c => !c.usage_desc],
    ['origin_country', 'ประเทศต้นกำเนิด', c => !c.origin_country],
    ['brand', 'แบรนด์', c => !c.brand],
  ];
  const tally = FIELDS
    .map(([k, label, miss]) => [label, partial.filter(li => miss(li.product_classification)).length])
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);

  const copySkus = () => {
    const t = missing.map(li => li.sku || li.product_classification?.sku).filter(Boolean).join('\n');
    navigator.clipboard?.writeText(t).catch(() => {});
  };

  return (
    <div className="gap noprint">
      <div className="gap-head">
        <b>ยังไม่ครบ {missing.length} รายการ</b>
        <span>จาก {items.length} รายการในใบนี้ · ปุ่มพิมพ์ยังใช้ได้ แต่ควรเติมก่อนส่ง broker</span>
      </div>

      {msg && <div className="gap-msg">{msg}</div>}

      {noCard.length > 0 && (
        <div className="gap-block new">
          <div className="gap-bt">＋ ยังไม่มีใบข้อมูลสินค้า {noCard.length} รายการ</div>
          <div className="gap-bs">
            สร้างใบให้ได้เลย — ระบบใส่ <b>รหัส · ชื่อจาก invoice · แบรนด์</b> ให้
            ส่วนวัสดุ คำบรรยาย และ<b>ประเทศผลิต ไม่เดาให้</b> — วัสดุตัดสินบรรทัดภาษี ·
            ประเทศผลิตตัดสินสิทธิ์ FTA (ส่งมาจากญี่ปุ่นไม่ได้แปลว่าผลิตที่ญี่ปุ่น) — สร้างใบแล้วกดปุ่ม “ประเทศผลิตจาก packing list” ด้านบนเพื่อเติมจากเอกสารจริง
          </div>
          <button className="btn1 sm" disabled={busy} onClick={() => onMakeStubs(noCard)}>
            {busy ? 'กำลังสร้าง…' : `สร้างใบข้อมูลสินค้าให้ ${noCard.length} รายการ`}
          </button>
        </div>
      )}

      {partial.length > 0 && (
        <div className="gap-block fix">
          <div className="gap-bt">! มีใบข้อมูลแล้ว แต่ยังขาดบางช่อง {partial.length} รายการ</div>
          <div className="gap-tally">
            {tally.map(([label, n]) => <span key={label} className="gap-pill">{label} <b>{n}</b></span>)}
          </div>
          <div className="gap-bs" style={{ marginTop: 10, marginBottom: 8 }}>
            รูป · หมวด · แบรนด์ ดึงจากทะเบียนสินค้ากลางได้ถ้ามี —
            {aiOn
              ? <> ส่วน <b>วัสดุ</b> ให้ AI ช่วยค้นจากเว็บผู้ผลิตได้ (คนตรวจก่อนลงสินค้าเสมอ) · <b>คืออะไร · ใช้ทำอะไร</b> ทะเบียนไม่มี ฝากช่วยกรอกเอง</>
              : <> ส่วน <b>วัสดุ · คืออะไร · ใช้ทำอะไร</b> ทะเบียนไม่มี ต้องมีคนกรอกพร้อมหลักฐาน</>}
          </div>
          <button className="btn2 sm" disabled={busy} onClick={() => onPullPd(partial)}>
            {busy ? 'กำลังดึง…' : `ดึงรูป/หมวด/แบรนด์ จากทะเบียนสินค้า (${partial.length} ใบ)`}
          </button>
          {noOrigin > 0 && (
            <button className="btn2 sm" disabled={busy} onClick={onPickOrigin} style={{ marginLeft: 8 }}
              title="packing list ที่มีคอลัมน์ Country of Origin — ระบบเติมประเทศผลิตให้ใบที่ยังว่าง ให้ตรวจก่อนบันทึก">
              อ่านประเทศผลิตจาก packing list ({noOrigin} ใบยังไม่มี)
            </button>
          )}
          {aiOn && noMaterial > 0 && (
            <MaterialGapButton consignmentIds={[consignment.id]} docDate={consignment.invoice_date ?? null}
              className="btn2 sm" style={{ marginLeft: 8 }} label={`หาวัสดุที่ยังว่าง (${noMaterial} ใบ)`}
              title="ให้ AI ช่วยค้นวัสดุจากเว็บผู้ผลิต — บอกจำนวนรุ่นและค่าใช้จ่ายก่อนเริ่ม ผลเป็นร่างจนกว่าคนจะกดยืนยัน"
              onMessage={onMessage} onOpenReview={onOpenMaterials}/>
          )}
          {aiOn && noMaterial > 0 && noCard.length > 0 && (
            <div className="gap-bs" style={{ marginTop: 8, marginBottom: 0 }}>
              อีก {noCard.length} รายการยังไม่มีใบข้อมูลสินค้า ค้นวัสดุให้ไม่ได้ — ฝากช่วยกด “สร้างใบข้อมูลสินค้า” ด้านบนก่อน
            </div>
          )}
        </div>
      )}

      <div className="gap-foot">
        <button className="btn2 sm" onClick={() => setOpen(o => !o)}>
          {open ? '▴ ซ่อนรายการ' : `▾ ดูรายการที่ยังไม่ครบ ${missing.length} รายการ`}
        </button>
        <button className="btn2 sm" onClick={copySkus}>⧉ คัดลอกรหัสทั้งหมด</button>
      </div>

      {open && (
        <div className="gap-list">
          {missing.map(li => {
            const c = li.product_classification;
            const gaps = c ? db.cardGaps(c) : ['ยังไม่มีใบข้อมูลสินค้า'];
            // รหัสของใบก่อน — li.sku เป็นรหัสตามที่พิมพ์ใน invoice ("AC 24SU10500CH") ส่วนใบอาจเป็น "AC-24SU10500CH"
            const sku = c?.sku || li.sku;
            return (
              <button key={li.id} className="gap-row" title="เปิดในแท็บ สินค้า & HS"
                onClick={() => sku && onOpenProduct?.(sku)}>
                <span className="gap-sku">{sku || '—'}</span>
                <span className="gap-nm"><NameTh c={c} fallback={li.description} missing/></span>
                <span className="gap-miss">ขาด {gaps.join(' · ')}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Dossier({ consignmentId, onBack, onOpenProduct, onOpenPermit, canAi = false, onOpenMaterials }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);       // โหลดพัง = เต็มหน้า
  const [notFound, setNotFound] = useState(false); // ลิงก์ชี้ใบที่ไม่มีแล้ว / ลิงก์ไม่ครบ — คนละเรื่องกับโหลดพัง
  const [actMsg, setActMsg] = useState(null);      // ลบ/เพิ่มพลาด = แถบเตือน ไม่ทำลายทั้งหน้า
  const [pending, setPending] = useState(false);
  const [picking, setPicking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);   // ผลนำเข้า invoice (ข้อมูล ไม่ใช่ error)
  const [signer, reloadSigner] = useSigner();
  const aiOn = useMaterialsReady(canAi);
  const fileRef = useRef(null);
  const [importFile, setImportFile] = useState(null);   // ไฟล์ที่เพิ่งเลือก รอให้คนตรวจก่อนบันทึก
  const [stubBusy, setStubBusy] = useState(false);
  const [stubMsg, setStubMsg] = useState(null);
  const [xlsBusy, setXlsBusy] = useState(null);
  const originRef = useRef(null);
  const permit = usePermitIndex();
  const [permitReqs, setPermitReqs] = useState(null);   // การ์ดเอกสารใบอนุญาตของใบนี้ · null = อ่านไม่ได้/ยังไม่โหลด
  const [pmOpen, setPmOpen] = useState({});             // แถวไหนเปิดเหตุผลของป้ายอยู่ (ใช้ key = line id)
  const [originFile, setOriginFile] = useState(null);   // packing list ที่เลือกมาอ่านประเทศผลิต (รอให้ตรวจก่อนบันทึก)   // ข้อความความคืบหน้าตอนทำไฟล์ Excel ให้ชิปปิ้ง (null = ว่าง)

  // สร้างใบข้อมูลสินค้าให้รายการที่ยังไม่มีในทะเบียน แล้วผูกกลับเข้าบรรทัดในตู้
  // ใส่เฉพาะที่เอกสารบอก (รหัส · ชื่อ · แบรนด์ · ประเทศต้นกำเนิดจาก invoice) ที่เหลือคนเติมเอง
  const makeStubs = async (lines) => {
    setStubBusy(true); setStubMsg(null);
    try {
      const c0 = data?.consignment;
      // ⚠ ไม่ส่งประเทศของ supplier ไปเป็นประเทศผลิต — ประเทศที่ส่งของมา ≠ ประเทศที่ผลิต (user 29 ต.ค. 69)
      const made = await db.createStubCards(
        lines.map(li => ({ sku: li.sku, description: li.description })),
        { brand: c0?.supplier_name ?? null });
      const linked = await db.linkLinesToCards(consignmentId, made);
      setStubMsg(`สร้างใบข้อมูลสินค้าให้ ${Object.keys(made).length} รายการ · ผูกเข้าตู้แล้ว ${linked} บรรทัด — เหลือเติม รูป · ชื่อไทย · คืออะไร · วัสดุ · ใช้ทำอะไร · ประเทศผลิต (กด "อ่านประเทศผลิตจาก packing list" ได้)`);
      load();
    } catch (e) { setStubMsg(`สร้างไม่สำเร็จ: ${e.message ?? String(e)}`); }
    setStubBusy(false);
  };

  // เติมรูป/หมวด/แบรนด์ จากทะเบียนสินค้ากลาง (PD) ให้ใบที่ยังว่าง — กดซ้ำได้เรื่อย ๆ
  // ของใหม่ที่ PD ยังไม่มีรูป พอวันหนึ่งมีรูป กดอีกทีก็ได้มาเอง ไม่ต้องไล่ทีละใบ
  const pullFromPd = async (lines) => {
    setStubBusy(true); setStubMsg(null);
    try {
      const cards = lines.map(li => li.product_classification).filter(Boolean);
      const r = await db.fillFromProductMaster(cards);
      const got = [r.photo && `รูป ${r.photo}`, r.category && `หมวด ${r.category}`, r.brand && `แบรนด์ ${r.brand}`]
        .filter(Boolean).join(' · ');
      setStubMsg(got
        ? `ดึงจากทะเบียนสินค้าได้: ${got} · ตรวจ ${r.checked} ใบ เจอในทะเบียน ${r.found}`
        : `ตรวจ ${r.checked} ใบ เจอในทะเบียน ${r.found} แต่ทะเบียนยังไม่มีรูป/หมวดให้ — ของใหม่ที่ยังไม่เคยเข้าคลังมักยังไม่มีรูป`);
      load();
    } catch (e) { setStubMsg(`ดึงไม่สำเร็จ: ${e.message ?? String(e)}`); }
    setStubBusy(false);
  };

  // PGRST116 = ไม่มีใบนี้ · 22P02 = id ในลิงก์ไม่ใช่รูป uuid (ก๊อปมาไม่ครบ) → บอกเป็นภาษาคน ไม่โชว์ข้อความ Postgres ดิบ
  const load = () => {
    db.listPermitReqs(consignmentId).then(setPermitReqs).catch(() => setPermitReqs(null));
    if (permit.status === 'failed') permit.retry();   // หน้าตรวจไฟล์สัญญาไว้ว่าจะลองอีกครั้งที่นี่
    return db.getDossier(consignmentId).then(setData)
      .catch(e => {
        if (isSchemaPending(e)) setPending(true);
        else if (e?.code === 'PGRST116' || e?.code === '22P02') setNotFound(true);
        else setError(e.message ?? String(e));
      });
  };
  useEffect(() => { if (!MOCK && consignmentId) load(); }, [consignmentId]);
  const removeItem = async (id) => {
    setActMsg(null);
    try { await db.deleteLineItem(id, data?.consignment?.shipment?.id); load(); }
    catch (e) { setActMsg(`เอาออกไม่สำเร็จ: ${e.message ?? String(e)}`); }
  };
  const onImportInvoice = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setActMsg(null); setImportMsg(null);
    // ไฟล์จากซัพ (PDF/Excel) → เปิดหน้าตรวจก่อน ไม่เขียนอะไรจนกว่าจะกดยืนยัน
    // ไฟล์ CSV/TXT ที่ทำจากเทมเพลตของเราเอง → ทางเดิม เพราะคอลัมน์ตรงอยู่แล้วไม่ต้องตรวจ
    if (!/\.(csv|txt)$/i.test(file.name)) { setImportFile(file); return; }
    setImporting(true);
    try {
      const lines = csvToRecords(await file.text(), INVOICE_COLMAP);
      if (!lines.length) throw new Error('ไม่พบข้อมูล — หัวคอลัมน์ต้องมี sku และ qty (โหลดเทมเพลตดูตัวอย่าง)');
      const r = await db.importInvoiceLines(consignmentId, lines, data?.consignment?.shipment?.id);
      setImportMsg(`ใส่ ${r.added} รายการ · จับคู่คลัง ${r.matched}`
        + (r.priced ? ` · มีราคา ${r.priced} บรรทัด (หน้าแบรนด์จะเทียบกับใบสั่งซื้อให้)` : ' · ไม่มีคอลัมน์ราคา — ใส่ unit_price ในไฟล์ไว้ด้วยจะเทียบกับที่สั่งได้')
        + (r.unmatched ? ` · ไม่พบในคลัง ${r.unmatched} (${r.unmatchedSkus.slice(0, 8).join(', ')}${r.unmatchedSkus.length > 8 ? '…' : ''}) — เพิ่มในสินค้า & HS แล้วแก้` : '')
        + ' · ตรวจ/แก้ก่อนพิมพ์');
      load();
    } catch (er) { setActMsg(`นำเข้าไม่สำเร็จ: ${er.message ?? String(er)}`); }
    setImporting(false);
  };
  const downloadInvoiceTemplate = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + INVOICE_TEMPLATE], { type: 'text/csv' }));
    a.download = 'template_invoice.csv'; a.click(); URL.revokeObjectURL(a.href);
  };

  if (MOCK) return <div className="placeholder"><h2>Product Dossier</h2><p>โหมด MOCK — ต่อ Supabase เพื่อสร้าง dossier จริง</p></div>;
  if (pending) return <DbPending/>;
  if (notFound) return (
    <div className="placeholder">
      <h2>ไม่พบใบข้อมูลสินค้านี้</h2><p>อาจถูกลบไปแล้ว หรือลิงก์ไม่ครบ</p>
      <button className="tab" onClick={() => onBack()}>กลับ Console</button>
    </div>
  );
  if (error) return (
    <div className="placeholder">
      <h2>โหลดไม่สำเร็จ</h2><p className="mono">{error}</p>
      <button className="tab" onClick={() => onBack()}>กลับ Console</button>
    </div>
  );
  if (!data) return <div className="placeholder"><h2>กำลังโหลด…</h2></div>;

  const { consignment: c, items } = data;
  const gateOk = ready(items);

  // Excel ส่งชิปปิ้ง — แบบเดียวกับที่ชิปปิ้งรับมาตลอด ชิปปิ้งกรอกพิกัด/อากร/ใบอนุญาตลงไฟล์นี้แล้วส่งกลับได้ (ดู lib/dossier-xlsx.js)
  const onExportXlsx = async () => {
    setActMsg(null); setImportMsg(null);
    setXlsBusy('กำลังเตรียมไฟล์…');
    try {
      const r = await buildDossierXlsx({ consignment: c, items },
        { onProgress: (d, n) => setXlsBusy(`ใส่รูป ${d}/${n}…`) });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.blob);
      a.download = r.fileName;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      setImportMsg(`ดาวน์โหลด ${r.fileName} แล้ว — ${r.rows} รายการ · มีรูป ${r.withPhoto}`
        + (r.noPhoto ? ` · ไม่มีรูป ${r.noPhoto}` : '')
        + (r.failed ? ` · โหลดรูปไม่ได้ ${r.failed} (กดใหม่อีกครั้งได้)` : '')
        + ' · ชิปปิ้งกรอกพิกัด / อากร / ใบอนุญาต ลงไฟล์นี้แล้วส่งกลับมาได้เลย');
    } catch (e) { setActMsg(`ทำไฟล์ Excel ไม่สำเร็จ: ${e.message ?? String(e)}`); }
    setXlsBusy(null);
  };
  // เรื่องใบอนุญาต: คำนวณสดจากใบสินค้า + กฎพิกัดทุกครั้ง จึงครอบทุกทางที่รายการเข้ามา (ไฟล์ · CSV · เลือกเอง · แก้พิกัดทีหลัง)
  const pmLines = permit.index ? items.map(li => {
    const cl = li.product_classification ?? null;
    return { key: li.id, sku: cl?.sku ?? li.sku, name: cl?.name_en || cl?.name_th || li.description,
      cardId: cl?.id ?? null, card: cl, result: checkCard(cl, permit.index, li.description) };
  }) : [];
  const pmById = Object.fromEntries(pmLines.map(l => [l.key, l.result]));
  const pmSum = permit.status === 'ok' ? summarizePermits(pmLines) : null;
  // ⚠ ตู้ปิดแล้ว: ไม่มีอะไรให้ทำต่อ และ non-admin แก้การ์ดไม่ได้ (trg_lock_closed) ไม่ควรเตือนค้างตลอดไป
  const shipClosed = ['received', 'delivered', 'void'].includes(c.shipment?.status);
  const pmPill = !items.length || permit.status === 'loading' ? null : permitPill(pmSum, permitReqs, { closed: shipClosed });

  const missing = items.filter(li => {
    const cl = li.product_classification;
    return !cl || !(cl.photos ?? []).length || !hasThai(cl.name_th) || !cl.what_is_it || !hasMaterial(cl)
      || !cl.usage_desc || !cl.origin_country || !cl.brand || (isElectrical(li) && !cl.power_spec);
  });

  return (
    <div className="page">
      <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.csv,.txt"
        style={{ display: 'none' }} onChange={onImportInvoice}/>
      <input ref={originRef} type="file" accept=".pdf,.xlsx,.xls,.csv"
        style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) { setActMsg(null); setImportMsg(null); setOriginFile(f); } }}/>
      <div className="page-h noprint">
        <button className="btn2" onClick={() => onBack(c.shipment?.code)}>← กลับ Console</button>
        <h2>ใบข้อมูลสินค้า (ส่ง broker)</h2>
        <span className="meta">{c.shipment?.code} · {c.supplier_name} · invoice {c.invoice_no || '—'}</span>
        <span className={`gatepill ${gateOk ? 'ok' : 'no'}`}>
          {gateOk ? 'ครบพร้อมส่ง broker' : items.length ? `ยังไม่ครบ ${missing.length} รายการ` : 'ยังไม่มีสินค้า'}
        </span>
        {pmPill && <span className={`gatepill ${pmPill.cls}`}>{pmPill.text}</span>}
        <button className="btn1" disabled={importing} onClick={() => fileRef.current?.click()}
          title="ลากไฟล์ invoice/PI ที่ซัพส่งมาได้เลย — PDF · Excel · CSV · ระบบอ่านแล้วให้ตรวจก่อนบันทึก">
          {importing ? 'กำลังนำเข้า…' : '⬆ อัปโหลด invoice (PDF · Excel)'}
        </button>
        <button className="btn2" onClick={downloadInvoiceTemplate}>เทมเพลต</button>
        <button className="btn2" onClick={() => setPicking(true)}>＋ เลือกเอง</button>
        <button className="btn2" disabled={!items.some(li => li.product_classification)} onClick={() => originRef.current?.click()}
          title={items.some(li => li.product_classification)
            ? 'อ่านคอลัมน์ Country of Origin ใน packing list แล้วเติมประเทศผลิตให้ใบข้อมูลสินค้า — ตรวจก่อนบันทึก'
            : 'ยังไม่มีใบข้อมูลสินค้าในใบนี้ — สร้างใบก่อน (แผง "ยังไม่ครบ" ด้านล่าง) แล้วค่อยอ่านประเทศผลิต'}>
          ประเทศผลิตจาก packing list</button>
        <SignerEditor signer={signer} onSaved={reloadSigner}/>
        <button className="btn2" disabled={!items.length || !!xlsBusy} onClick={onExportXlsx}
          title="ไฟล์ Excel แบบเดียวกับที่ชิปปิ้งใช้: No · Pic · Item No. · Name · Description · Materials · HS Code · Duty · License — ชิปปิ้งกรอกลงไฟล์นี้แล้วส่งกลับได้">
          {xlsBusy || '⬇ Excel ส่งชิปปิ้ง'}</button>
        <button className="btn1" disabled={!items.length} onClick={() => window.print()}>พิมพ์ / บันทึก PDF</button>
      </div>

      {importMsg && <div className="dossier-warn noprint" style={{ borderColor: '#2f6bff', color: '#1e40af', background: '#eef4ff' }}>{importMsg}</div>}
      {actMsg && <div className="dossier-warn noprint" style={{ borderColor: '#ff5470', color: '#e5254a' }}>{actMsg}</div>}

      {!items.length && (
        <div className="firststep noprint" style={{ textAlign: 'center' }}>
          <div className="fs-t">ใส่รายการสินค้าเข้าใบนี้ — 2 ทาง</div>
          <div className="fs-steps" style={{ alignItems: 'center' }}>
            <div className="fs-step"><b>①</b> <strong>เร็วสุด:</strong> อัปโหลดไฟล์ invoice/packing (SKU + จำนวน) → ระบบจับคู่กับคลังแล้วเติมให้อัตโนมัติ → ตรวจ/แก้</div>
            <div className="fs-step"><b>②</b> หรือ กด "＋ เลือกเอง" ติ๊กสินค้าจากคลังทีละรายการ</div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn1" disabled={importing} onClick={() => fileRef.current?.click()}>
              {importing ? 'กำลังนำเข้า…' : '⬆ อัปโหลด invoice'}
            </button>
            <button className="btn2" onClick={downloadInvoiceTemplate}>โหลดเทมเพลต</button>
            <button className="btn2" onClick={() => setPicking(true)}>＋ เลือกเอง</button>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <PermitPanel mode="dossier" memoKey={c.id} closed={shipClosed} status={permit.status} lines={pmLines} reqs={shipClosed ? null : permitReqs} onRetry={permit.retry}
          onOpenPermit={onOpenPermit ? (li) => onOpenPermit(li.card) : undefined}
          onMakeCards={shipClosed || !c.shipment?.id ? undefined : async () => {
            setActMsg(null);
            try {
              await db.computeRequirements(c.shipment.id);
              const fresh = await db.listPermitReqs(c.id);
              setPermitReqs(fresh);
              const still = (pmSum?.agencies ?? []).filter(a => a.must > 0 && agencyCardState(a.agency, fresh).key === 'none');
              // compute จับคู่พิกัดแบบตรงตัว: พิกัด 8 หลัก/หมายเหตุในทะเบียน สร้างช่องไม่ได้ ต้องบอกว่าทำไมปุ่มไม่มีผล
              if (still.length) setActMsg(`ยังสร้างช่องของ ${still.map(a => a.meta.chip).join(' · ')} ไม่ได้ — ระบบสร้างช่องให้เฉพาะรายการที่พิกัดครบ 11 หลักและตรงกับกฎพิกัด ฝากช่วยตรวจพิกัดในใบข้อมูลสินค้า`);
            } catch (e) { setActMsg(`สร้างช่องแนบไม่สำเร็จ: ${e.message ?? String(e)}`); }
          }}/>
      )}

      {items.length > 0 && !gateOk && (
        <GapPanel items={items} missing={missing} consignment={c} busy={stubBusy}
          onMakeStubs={makeStubs} onPullPd={pullFromPd} onPickOrigin={() => originRef.current?.click()}
          onOpenProduct={onOpenProduct} msg={stubMsg}
          aiOn={aiOn} onMessage={setStubMsg} onOpenMaterials={onOpenMaterials}/>
      )}

      {originFile && (
        <OriginFromFile file={originFile} items={items} onClose={() => setOriginFile(null)}
          onDone={(summary, failed) => { if (failed) setActMsg(summary); else setImportMsg(summary); load(); }}/>
      )}

      {importFile && (
        <ImportInvoiceModal file={importFile} consignment={c}
          onClose={() => setImportFile(null)}
          onDone={(r, newOnes, asked) => { load(); setImportMsg(
            `ใส่ ${r.added} รายการ · จับคู่ทะเบียนได้ ${r.matched}`
            + (newOnes.length ? ` · ยังไม่เคยมี ${newOnes.length} รายการ (${newOnes.slice(0,5).map(n => n.sku).join(', ')}${newOnes.length > 5 ? '…' : ''}) — เพิ่มที่แท็บ สินค้า & HS` : '')
            + (asked ? ` · เรื่องใบอนุญาต: ${asked} รายการ ดูกล่องเรื่องใบอนุญาตด้านล่าง` : '')
            + ' · ตรวจ/แก้ก่อนพิมพ์'); }}/>
      )}
      {picking && (
        <AddLineItemModal consignment={c} searchProducts={db.listClassifications}
          onClose={() => setPicking(false)}
          onSubmit={async records => { await db.addLineItems(c.id, records, c.shipment?.id); load(); }}/>
      )}

      <div className="a4">
        <div className="a4-head">
          <div>
            <div className="a4-brand">{DOC_HEAD[0]}</div>
            <div className="a4-org">{DOC_HEAD[1]}</div>
            <div className="a4-org">{DOC_HEAD[2]}</div>
            <div className="a4-tax">เอกสารประกอบการตีพิกัดศุลกากร / Product Dossier for Tariff Classification</div>
          </div>
          <div className="a4-meta">
            <div>Shipment: <b>{c.shipment?.code}</b></div>
            <div>Supplier: <b>{c.supplier_name}</b></div>
            <div>Invoice: <b>{c.invoice_no || '—'}</b> · {c.origin_country || '—'}</div>
            <div>Mode: {c.shipment?.mode?.toUpperCase()} · ETA {c.shipment?.eta || '—'}</div>
          </div>
        </div>

        <table className="a4-table">
          <thead>
            <tr>
              <th style={{ width: 78 }}>รูป</th>
              <th>สินค้า / รายละเอียด</th>
              <th style={{ width: 92 }}>จำนวน</th>
              <th style={{ width: 150 }}>HS + อัตราอากร<br/><span className="brokerfill">(broker กรอก)</span></th>
            </tr>
          </thead>
          <tbody>
            {items.map(li => {
              const cl = li.product_classification ?? {};
              return (
                <tr key={li.id}>
                  <td>{(cl.photos ?? [])[0]
                    ? <img className="a4-img" src={cl.photos[0].url} alt=""/>
                    : <div className="a4-noimg">ไม่มีรูป</div>}</td>
                  <td>
                    {/* ชื่ออังกฤษ = ตัวหลัก (ตัวเข้ม, นิ่ง) · ชื่อไทยสำแดง = บรรทัดแยก แก้ได้โดยไม่กระทบตัวเข้ม */}
                    <div className="a4-nm">{cl.name_en || cl.name_th || li.description || '—'}</div>
                    {cl.name_th && cl.name_th !== (cl.name_en || '') && <div className="a4-th">ไทย: {cl.name_th}</div>}
                    <div className="a4-en">{[cl.brand, cl.sku ? `รหัส ${cl.sku}` : null].filter(Boolean).join(' · ')}</div>
                    <div className="a4-pm noprint">
                      <PermitChips result={pmById[li.id]} open={!!pmOpen[li.id]} onToggle={() => setPmOpen(o => ({ ...o, [li.id]: !o[li.id] }))}/>
                    </div>
                    <div className="a4-desc">
                      {cl.what_is_it && <div><b>คืออะไร/ใช้ทำอะไร:</b> {cl.what_is_it}</div>}
                      {hasMaterial(cl) && <div><b>ทำจาก:</b> {cl.material}</div>}
                      {cl.usage_desc && <div><b>วิธีใช้:</b> {cl.usage_desc}</div>}
                      {cl.origin_country && <div><b>ถิ่นกำเนิด:</b> {cl.origin_country}</div>}
                      {cl.power_spec && <div><b>สเปคไฟ:</b> {cl.power_spec}</div>}
                      {cl.is_dangerous_goods && <div className="a4-dg">⚠ DANGEROUS GOODS</div>}
                    </div>
                  </td>
                  <td className="a4-qty">{Number(li.qty).toLocaleString()} {li.unit || ''}</td>
                  <td>{cl.hs_code
                    ? <div className="a4-hs">{cl.hs_code}{cl.hs_confirmed ? '' : ' (รอยืนยัน)'}</div>
                    : <div className="a4-hsblank">………………………</div>}
                    <button className="rowdel noprint" title="เอาสินค้านี้ออกจากใบ" onClick={() => removeItem(li.id)}>✕ เอาออก</button>
                  </td>
                </tr>
              );
            })}
            {!items.length && <tr><td colSpan={4} className="a4-empty">ยังไม่มีสินค้าในตู้นี้ — กลับไป Console → กลุ่ม supplier → กดปุ่ม "＋ สินค้า" เพื่อเลือกสินค้าจากคลังใส่เข้าตู้ก่อน</td></tr>}
          </tbody>
        </table>

        <div className="a4-sign">
          <div>ผู้จัดทำ / Prepared by <b>{signer?.full_name || '______________________'}</b>{signer?.job_title ? ` (${signer.job_title})` : ''} วันที่ __________</div>
          <div>ผู้ตีพิกัด / Customs Broker ______________________ วันที่ __________</div>
        </div>
        <div className="a4-foot">
          ข้อมูลนี้ช่วยคัดกรองเบื้องต้น — พิกัด/อัตราอากร/เงื่อนไขจริงยึดตามกรมศุลกากร / broker / Advance Ruling ·
          สร้างจาก INBOUNDpoint
        </div>
      </div>
    </div>
  );
}
